import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import ChartData from '../../../common/model/type/chart-data.type';
import Variables from '../../../design-system/variables';
import Colours from '../../../design-system/colours';
import { isNumber } from 'lodash';
import { Constants } from '../../../common/constants';
import DateUtils from '../../../common/utils/services/date-utils';
import { HormoneDetailsUtils } from '../../../common/utils/services/hormone-details-utils';
import { CalendarDayStatusUtils } from '../../../common/utils/services/calendar-day-status-utils';
import OvProviderSourceBadge from './OvProviderSourceBadge';
import OvWearableDataSummary from './OvWearableDataSummary';

export interface OvHormoneDetailsProps {
  data: ChartData;
  heading?: string;
  healthGoal?: string;
  onClose?: () => void;
}

type HormoneDetailsSeriesId = 'lh' | 'pg' | 'lr_e3g';

const HORMONE_DETAILS_SERIES: {
  id: HormoneDetailsSeriesId;
  displayName: string;
  unit: string;
}[] = [
  { id: 'lh', displayName: 'LH', unit: Constants.LH_UNIT_OF_MEASURE },
  { id: 'pg', displayName: 'PdG', unit: Constants.PG_UNIT_OF_MEASURE },
  { id: 'lr_e3g', displayName: 'E3G', unit: Constants.E3G_UNIT_OF_MEASURE },
];

const OvHormoneDetails: FC<OvHormoneDetailsProps> = ({
  data,
  heading,
  healthGoal,
  onClose,
}) => {
  const { t } = useTranslation();

  const tryingToConceiveStatus =
    CalendarDayStatusUtils.getTryingToConceiveStatusLabel(
      data.dailyData?.trying_to_conceive_status,
      healthGoal
    );
  const statusColor = CalendarDayStatusUtils.getTryingToConceiveStatusColor(
    data.dailyData?.trying_to_conceive_status
  );

  const renderHormoneValues = (
    seriesId: HormoneDetailsSeriesId,
    unit: string
  ) => {
    const dailyValue = data.dailyData?.[seriesId];

    if (!isNumber(dailyValue)) {
      return '-';
    }

    const scans = data.dailyData?.scans?.filter((scan) =>
      isNumber(scan?.[seriesId])
    );

    if (!scans?.length) {
      return (
        <StyledDataText>
          {dailyValue} {unit}
        </StyledDataText>
      );
    }

    return scans.map((scan, index) => (
      <StyledDataText key={`${scan?.[seriesId]}-${index}`}>
        {`${Math.max(scan?.[seriesId] ?? 0, 0)} ${unit} - ${
          scan.created_at && DateUtils.formatDate(scan.created_at, 'HH:mm')
        }`}
      </StyledDataText>
    ));
  };

  return (
    <Container>
      <Header>
        <HeaderText>
          {heading ??
            `${t('dashboard.chart.day')} ${data?.index}${
              data?.dateStr ? ' - ' : ''
            }${data?.dateStr}`}
        </HeaderText>
        {onClose && (
          <StyledCloseIconButton aria-label="Close" onClick={onClose}>
            <CloseIcon />
          </StyledCloseIconButton>
        )}
      </Header>
      {tryingToConceiveStatus && (
        <StyledFertilityStatus
          style={{
            backgroundColor: statusColor,
            // Unlike OvDailyDataDetailsInCalendar's dark sidecart, this panel
            // sits on a light (OV_WHITE) card, so text/border always stay
            // dark — a white-on-transparent LOW pill would be invisible here.
            color: Colours.OV_BASE,
            border: `1px solid ${Colours.OV_BASE}`,
          }}
        >
          {tryingToConceiveStatus}
        </StyledFertilityStatus>
      )}
      <ContentSection>
        <SectionHeader>
          <Label>{t('common.hormone')}</Label>
        </SectionHeader>
        <DataContainer>
          {HORMONE_DETAILS_SERIES.map((series) => (
            <DataGroup key={series.id}>
              <Label>{series.displayName}</Label>
              {renderHormoneValues(series.id, series.unit)}
            </DataGroup>
          ))}
        </DataContainer>

        <SectionHeader>
          <Label>{t('common.nonHormoneData')}</Label>
        </SectionHeader>
        <DataContainer>
          {HormoneDetailsUtils.detailRows.map((row, index) => {
            const journalEntry = data?.dailyData?.journal_entry;
            const value = row.getValue(data);

            if (!value) {
              return null;
            }

            const formattedValue = (
              row.formatValue ?? HormoneDetailsUtils.formatDefaultValue
            )(value);
            const autofillProvider = HormoneDetailsUtils.getAutofillProvider(
              journalEntry,
              row
            );

            return (
              <React.Fragment key={`${row.label}-${index}`}>
                <DataGroup>
                  <LabelRow>
                    <Label>{t(row.label)}</Label>
                    {autofillProvider ? (
                      <OvProviderSourceBadge provider={autofillProvider} />
                    ) : null}
                  </LabelRow>
                  {formattedValue ? (
                    <StyledDataText>{formattedValue}</StyledDataText>
                  ) : (
                    '-'
                  )}
                </DataGroup>
                {row.showWearableSummaryAfter ? (
                  <StyledWearableDataSummary
                    journalEntry={journalEntry}
                    compact
                    variant="light"
                  />
                ) : null}
              </React.Fragment>
            );
          })}
          {!data?.dailyData?.journal_entry?.hours_of_sleep ? (
            <StyledWearableDataSummary
              journalEntry={data?.dailyData?.journal_entry}
              compact
              variant="light"
            />
          ) : null}
        </DataContainer>
      </ContentSection>
    </Container>
  );
};

export default OvHormoneDetails;

const Container = styled.section`
  position: relative;
  box-shadow: ${Variables.boxShadow.defaultBox};
  border-radius: ${Variables.borderRadius.LARGE};
  border: 1px solid ${Colours.OV_BORDER_COLOR_V3};
  background-color: ${Colours.OV_WHITE};
  padding: 1rem 1rem 0 1rem;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${Colours.OV_BORDER_COLOR_V3};
  margin: 0 -1rem;
  padding: 0 1rem 1rem 1rem;
`;

const StyledCloseIconButton = styled(IconButton)`
  && {
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
    padding: 0;
    margin: 0;
    color: ${Colours.OV_BASE};
  }
`;

const HeaderText = styled.p`
  font-weight: bold;
  margin: 0;
  font-size: ${Variables.fontSizes.MEDIUM};
  color: ${Colours.OV_BASE};
`;

const Label = styled.p`
  font-weight: 500;
  margin: 0;
  font-size: ${Variables.fontSizes.MEDIUM};
  color: ${Colours.OV_CHARCOAL};
`;

const StyledDataText = styled.p`
  margin-top: 0.25rem;
  margin-bottom: 0;
  font-weight: bold;
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ContentSection = styled.section`
  margin: 0 -1rem;
  padding: 0 1rem;
`;

const SectionHeader = styled.header`
  padding: 1rem 0;
`;

const DataContainer = styled.div`
  background-color: ${Colours.OV_ACCENT_TEAL_TINT};
  padding: 1rem;
  margin: 0 -1rem;
`;

const DataGroup = styled.div`
  margin-bottom: 0.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const StyledWearableDataSummary = styled(OvWearableDataSummary)`
  margin: 0.375rem 0 0.875rem;
`;

const StyledFertilityStatus = styled.div`
  display: inline-block;
  margin: 0.75rem 0;
  padding: 0.5rem 1rem;
  border-radius: ${Variables.borderRadius.XLARGE};
`;
